import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";

/**
 * Award-worthy division chapter with heavy motion:
 * - Section background morphs to accent tint as it enters
 * - Number counter animates from 00 → division index on enter
 * - Logo enters with stamp/rotate
 * - Image reveals with clip-path curtain (top → bottom)
 * - Services list staggers in
 * - Big drifting index number in the background with parallax
 * - Signature word ("PRINT", "STICK"…) crosses on scroll
 */
const DivisionChapter = ({ division, total, invert = false }) => {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-40% 0px -40% 0px", once: false });
  const [count, setCount] = useState(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1, 1.05]);
  const numberX = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const numberRotate = useTransform(scrollYProgress, [0, 1], [-4, 4]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.35, 0.65, 1],
    [0, 0.5, 0.5, 0]
  );
  const signatureX = useTransform(scrollYProgress, [0, 1], ["25vw", "-70vw"]);
  const signatureOpacity = useTransform(
    scrollYProgress,
    [0, 0.4, 0.75, 1],
    [0, 0.15, 0.15, 0]
  );

  const kicker = division.kicker[lang];
  const tagline = division.tagline[lang];
  const body = division.body[lang];
  const services = division.services[lang];
  const targetNumber = parseInt(division.index, 10);

  // Counter animation
  useEffect(() => {
    if (!inView) return undefined;
    let raf = 0;
    const start = performance.now();
    const dur = 900;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * targetNumber));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, targetNumber]);

  return (
    <section
      ref={ref}
      id={`chapter-${division.slug}`}
      data-testid={HOME.chapter(division.slug)}
      className="relative min-h-screen w-full px-6 md:px-10 py-24 md:py-32 flex flex-col justify-center border-t border-white/10 overflow-hidden"
      style={{
        "--accent": division.accent,
        "--accent-soft": division.accentSoft,
      }}
    >
      {/* Section-level color wash */}
      <motion.div
        style={{
          background: `radial-gradient(circle at 30% 40%, ${division.accentSoft}, transparent 60%), radial-gradient(circle at 80% 90%, ${division.accentSoft}, transparent 55%)`,
          opacity: bgOpacity,
        }}
        className="absolute inset-0 pointer-events-none"
      />

      {/* Signature word behind (kinetic breaker inside chapter) */}
      <motion.div
        style={{
          x: signatureX,
          opacity: signatureOpacity,
          color: division.accent,
        }}
        className="pointer-events-none absolute top-1/2 -translate-y-1/2 font-display uppercase text-[32vw] leading-none tracking-tighter whitespace-nowrap select-none"
        aria-hidden="true"
      >
        {division.name}
      </motion.div>

      {/* Big drifting index number */}
      <motion.div
        style={{ x: numberX, rotate: numberRotate, color: division.accent }}
        className="pointer-events-none absolute -top-4 md:top-4 left-2 md:left-6 font-display text-[28vw] md:text-[20vw] leading-none opacity-[0.09] select-none"
      >
        {division.index}
      </motion.div>

      <div className="relative max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — Text */}
        <div className={`lg:col-span-6 ${invert ? "lg:order-2" : ""}`}>
          {/* animated kicker row */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, margin: "-20%" }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-4 text-[0.68rem] tracking-hair uppercase mb-6"
            style={{ color: division.accent }}
          >
            <span className="font-display text-3xl md:text-4xl leading-none">
              {count.toString().padStart(2, "0")}
            </span>
            <span
              className="inline-block flex-1 max-w-[10rem] h-px"
              style={{ background: division.accent }}
            />
            <span>
              /{total.toString().padStart(2, "0")} — {kicker}
            </span>
          </motion.div>

          {/* Logo stamp */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 1.15, rotate: -4 }}
            whileInView={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
            viewport={{ once: false, margin: "-20%" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6"
            data-testid={HOME.chapterLogo(division.slug)}
          >
            {division.logo ? (
              <img
                src={division.logo}
                alt={`${division.name} logo`}
                className={`${division.logoClass || "h-32 md:h-44 lg:h-56"} w-auto object-contain`}
                style={{
                  mixBlendMode: division.logoBlend || "normal",
                  filter: `drop-shadow(0 0 30px ${division.accentSoft})`,
                }}
              />
            ) : (
              <div
                className="font-editorial italic text-6xl md:text-7xl lg:text-8xl"
                style={{ color: division.accent }}
              >
                {division.name}
              </div>
            )}
          </motion.div>

          {/* Tagline — split words */}
          <h2 className="font-editorial italic text-white text-4xl md:text-6xl lg:text-7xl leading-[1] mb-6">
            {tagline.split(" ").map((word, i) => (
              <span key={i} className="mask-line inline-block mr-3">
                <motion.span
                  initial={{ y: "115%" }}
                  whileInView={{ y: "0%" }}
                  viewport={{ once: false, margin: "-25%" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.1 + i * 0.06,
                    ease: [0.76, 0, 0.24, 1],
                  }}
                  className="inline-block"
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, margin: "-20%" }}
            transition={{ duration: 1, delay: 0.35 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-lg mb-8"
          >
            {body}
          </motion.p>

          <div className="mb-8">
            <div className="text-[0.65rem] tracking-hair uppercase text-white/50 mb-3">
              {t.divisions.services}
            </div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5 max-w-md">
              {services.map((s, i) => (
                <motion.li
                  key={s}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, margin: "-20%" }}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.06 }}
                  className="text-white/85 text-sm md:text-base flex items-center gap-2"
                >
                  <motion.span
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: false, margin: "-20%" }}
                    transition={{
                      duration: 0.35,
                      delay: 0.45 + i * 0.06,
                      type: "spring",
                      stiffness: 400,
                    }}
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: division.accent }}
                  />
                  {s}
                </motion.li>
              ))}
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, margin: "-20%" }}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 text-[0.7rem] tracking-hair uppercase text-white border-b border-white/40 hover:border-white pb-1 transition-colors group"
            >
              {t.divisions.viewContact}
              <span className="inline-block transition-transform group-hover:translate-x-1">
                →
              </span>
            </Link>
          </motion.div>
        </div>

        {/* RIGHT — Video (if present) hero + image + accent square */}
        <div className={`lg:col-span-6 ${invert ? "lg:order-1" : ""}`}>
          {division.video ? (
            /* Layout WITH video: big 16:9 video on top, image + number strip below */
            <div className="flex flex-col gap-3 md:gap-4">
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: false, margin: "-20%" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                data-testid={HOME.chapterVideo(division.slug)}
                className="relative aspect-video overflow-hidden border border-white/10 bg-black group"
              >
                <video
                  src={division.video}
                  poster={division.image}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Vignette + accent tint */}
                <div
                  className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-60"
                  style={{
                    background: `radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.7) 100%), linear-gradient(140deg, transparent 40%, ${division.accentSoft} 100%)`,
                  }}
                />
                {/* Corner: LIVE dot */}
                <div className="absolute top-3 right-3 flex items-center gap-2 text-[0.55rem] tracking-hair uppercase text-white/85">
                  <motion.span
                    animate={{ opacity: [1, 0.2, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: division.accent }}
                  />
                  REC · {division.index}
                </div>
                {/* Bottom label */}
                <div className="absolute bottom-3 left-3 flex items-center gap-3 text-[0.6rem] tracking-hair uppercase text-white/85">
                  <span>{t.divisions.video}</span>
                  <span className="opacity-40">·</span>
                  <span>{division.name}</span>
                </div>
                {/* Timecode */}
                <div className="absolute bottom-3 right-3 text-[0.55rem] tracking-hair uppercase text-white/60 font-mono">
                  00:00 / ∞
                </div>
              </motion.div>

              <div className="grid grid-cols-6 gap-3 md:gap-4">
                {/* Small still image (always visible, simple fade-in) */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="col-span-4 relative aspect-[3/2] overflow-hidden border border-white/10"
                >
                  <motion.img
                    style={{ y: imageY, scale: imageScale }}
                    src={division.image}
                    alt={division.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0 mix-blend-overlay opacity-70 pointer-events-none"
                    style={{
                      background: `linear-gradient(140deg, transparent 40%, ${division.accentSoft} 100%)`,
                    }}
                  />
                  <div className="absolute bottom-2 left-2 text-[0.55rem] tracking-hair uppercase text-white/70">
                    {division.index} · {division.name}
                  </div>
                </motion.div>
                {/* Accent number square */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: false, margin: "-20%" }}
                  transition={{ duration: 0.7, delay: 0.35 }}
                  className="col-span-2 relative aspect-[3/2] border border-white/10 flex items-center justify-center overflow-hidden"
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `radial-gradient(circle, ${division.accentSoft}, transparent 70%)`,
                    }}
                  />
                  <div
                    className="font-display text-5xl md:text-6xl lg:text-7xl relative"
                    style={{ color: division.accent }}
                  >
                    {division.index}
                  </div>
                </motion.div>
              </div>
            </div>
          ) : (
            /* Layout WITHOUT video: original placeholder shells */
            <div className="grid grid-cols-6 gap-3 md:gap-4">
              <div className="col-span-6 md:col-span-4 relative aspect-[4/5] overflow-hidden border border-white/10">
                <motion.div
                  initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
                  whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
                  viewport={{ once: false, margin: "-25%" }}
                  transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
                  className="absolute inset-0"
                >
                  <motion.img
                    style={{ y: imageY, scale: imageScale }}
                    src={division.image}
                    alt={division.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </motion.div>
                <motion.div
                  initial={{ scaleY: 1 }}
                  whileInView={{ scaleY: 0 }}
                  viewport={{ once: false, margin: "-25%" }}
                  transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
                  className="absolute inset-0 origin-top pointer-events-none"
                  style={{ background: division.accent }}
                />
                <div
                  className="absolute inset-0 mix-blend-overlay opacity-70 pointer-events-none"
                  style={{
                    background: `linear-gradient(140deg, transparent 40%, ${division.accentSoft} 100%)`,
                  }}
                />
                <div className="absolute bottom-3 left-3 text-[0.6rem] tracking-hair uppercase text-white/70">
                  {division.index} · {division.name}
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-2 text-[0.55rem] tracking-hair uppercase text-white/60">
                  <motion.span
                    animate={{ opacity: [1, 0.2, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: division.accent }}
                  />
                  LIVE
                </div>
              </div>

              <div className="col-span-6 md:col-span-2 flex flex-col gap-3 md:gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, margin: "-20%" }}
                  transition={{ duration: 0.9, delay: 0.4 }}
                  className="video-shell flex-1 flex items-center justify-center"
                  data-testid={HOME.chapterVideo(division.slug)}
                  style={{ minHeight: 120 }}
                >
                  <div className="text-center px-3">
                    <div
                      className="text-[0.55rem] tracking-hair uppercase mb-1 flex items-center justify-center gap-1.5"
                      style={{ color: division.accent }}
                    >
                      <motion.span
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.4, repeat: Infinity }}
                        className="inline-block w-1 h-1 rounded-full"
                        style={{ background: division.accent }}
                      />
                      REC
                    </div>
                    <div className="text-white/60 text-[0.6rem] leading-tight">
                      {t.divisions.videoSoon}
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: false, margin: "-20%" }}
                  transition={{ duration: 0.7, delay: 0.55 }}
                  className="relative aspect-square border border-white/10 flex items-center justify-center overflow-hidden"
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `radial-gradient(circle, ${division.accentSoft}, transparent 70%)`,
                    }}
                  />
                  <div
                    className="font-display text-6xl md:text-7xl relative"
                    style={{ color: division.accent }}
                  >
                    {division.index}
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DivisionChapter;
